
import React from "react";
import { useOutletContext } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ProfileCompletionCard } from "@/components/nursing-homes/owner/dashboard/ProfileCompletionCard";
import { Calendar, Home, Image, MapPin, Phone, User } from "lucide-react";

type ContextType = { nursingHome: NursingHome };

export default function OverviewPage() {
  const { nursingHome } = useOutletContext<ContextType>();

  // Calculate stats for the dashboard
  const contactInfoComplete = !!(
    nursingHome.email && 
    nursingHome.standardized_phone_number
  );
  
  const addressComplete = !!(
    nursingHome.street_name && 
    nursingHome.town && 
    nursingHome.county
  );
  
  const facilitiesCount = (nursingHome.facilities?.length || 0) + 
                          (nursingHome.amenities?.length || 0) + 
                          (nursingHome.services?.length || 0);
  
  // This would come from the leads table in a real implementation
  const activeLeads = 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{nursingHome.center_name}</h1>
          <p className="text-muted-foreground">
            {nursingHome.town}, {nursingHome.county}
          </p>
        </div>
        <ProfileCompletionCard nursingHome={nursingHome} />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Profile Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Active</div>
            <p className="text-xs text-muted-foreground mt-1">
              Your profile is visible to potential residents
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeLeads}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {activeLeads === 0 
                ? "No active leads at the moment" 
                : `${activeLeads} potential residents interested`}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Features Added</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{facilitiesCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {facilitiesCount === 0 
                ? "No features added yet" 
                : `${facilitiesCount} facilities, amenities & services`}
            </p>
          </CardContent>
        </Card>
      </div>
      
      <h2 className="text-xl font-semibold mt-8">Setup Progress</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className={contactInfoComplete ? "border-green-500" : "border-amber-300"}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`rounded-full p-2 ${contactInfoComplete ? "bg-green-100" : "bg-amber-100"}`}>
              <Phone className={`h-5 w-5 ${contactInfoComplete ? "text-green-600" : "text-amber-600"}`} />
            </div>
            <div>
              <h3 className="font-medium">Contact Information</h3>
              <p className="text-sm text-muted-foreground">
                {contactInfoComplete ? "Complete" : "Incomplete"}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={addressComplete ? "border-green-500" : "border-amber-300"}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`rounded-full p-2 ${addressComplete ? "bg-green-100" : "bg-amber-100"}`}>
              <MapPin className={`h-5 w-5 ${addressComplete ? "text-green-600" : "text-amber-600"}`} />
            </div>
            <div>
              <h3 className="font-medium">Address Details</h3>
              <p className="text-sm text-muted-foreground">
                {addressComplete ? "Complete" : "Incomplete"}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={facilitiesCount > 0 ? "border-green-500" : "border-amber-300"}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`rounded-full p-2 ${facilitiesCount > 0 ? "bg-green-100" : "bg-amber-100"}`}>
              <Home className={`h-5 w-5 ${facilitiesCount > 0 ? "text-green-600" : "text-amber-600"}`} />
            </div>
            <div>
              <h3 className="font-medium">Facilities & Services</h3>
              <p className="text-sm text-muted-foreground">
                {facilitiesCount > 0 ? `${facilitiesCount} Added` : "None Added"}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={nursingHome.main_image_url ? "border-green-500" : "border-amber-300"}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`rounded-full p-2 ${nursingHome.main_image_url ? "bg-green-100" : "bg-amber-100"}`}>
              <Image className={`h-5 w-5 ${nursingHome.main_image_url ? "text-green-600" : "text-amber-600"}`} />
            </div>
            <div>
              <h3 className="font-medium">Photos</h3>
              <p className="text-sm text-muted-foreground">
                {nursingHome.main_image_url ? "Main Photo Added" : "No Photos"}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-gray-300">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-full p-2 bg-gray-100">
              <User className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h3 className="font-medium">Staff Management</h3>
              <p className="text-sm text-muted-foreground">Coming Soon</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-gray-300">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-full p-2 bg-gray-100">
              <Calendar className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h3 className="font-medium">Calendar</h3>
              <p className="text-sm text-muted-foreground">Coming Soon</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
